import { FC, useEffect, useState } from 'react';
import styles from './Motivation.module.scss'
import { Table, Select, Space, Button, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, PlusOutlined, UndoOutlined } from '@ant-design/icons';
import { DateTime } from 'luxon';
import { IIsgGoods, IIsgGoodsTableProps, IMessageModalProps } from './.interface'
import { v4 as uuidv4 } from "uuid"
import { useTranslation } from 'react-i18next';
import renderGlobalAlert from '../../../../../system/hooks/useGlobalAlert';
import { MessageModalWindow } from '../../../../../components/modalWindows/MessageModalWindow';

const GoodsTable: FC<IIsgGoodsTableProps> = ({
    data,
    onChange,
    contractorMotivationId,
    onSaveSuccess,
    disabled,
    onEditingChange,
    tableName
}) => {
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editingRow, setEditingRow] = useState<IIsgGoods | null>(null);
    const isRowDeleted = (record: IIsgGoods) => {
        return record.dateDeleted !== null;
    };
    const isEditing = editingId !== null;
    const { t } = useTranslation();
    const baseT = (value: string) => t('directory.contractors.motivationTab.' + value);
    
    const priceCodeOptions = [
        { label: baseT('priceCodeOptions.purchasePriceWithVat'), value: 0 },
        { label: baseT('priceCodeOptions.retailPriceWithVat'), value: 1 },
        { label: baseT('priceCodeOptions.retailPriceWithoutDiscount'), value: 4 },
        { label: baseT('priceCodeOptions.directMargin'), value: 2 },
        { label: baseT('priceCodeOptions.directMarginWithoutDiscount'), value: 5 },
        { label: baseT('priceCodeOptions.totalMargin'), value: 3 },
        { label: baseT('priceCodeOptions.totalMarginWithoutDiscount'), value: 6 },
    ];

    const [showMessageModal, setShowMessageModal] = useState<IMessageModalProps>({
            show: false,
            message: "",
            hideCancelButton: false,
            idRow: "" 
        });

    const getPriceCodeText = (code: number): string => {
        const option = priceCodeOptions.find(opt => opt.value === code);
        return option?.label || baseT('goodsTable.notSelected');
    };

    const startEdit = (record: IIsgGoods) => {
        if (disabled) return
        if (isEditing) {
            message.warning(baseT('editWarning'));
            return;
        }
        setEditingId(record.idGlobal);
        setEditingRow({ ...record });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditingRow(null);
    };

    const saveEdit = () => {
        if (!editingRow || disabled) return;

        const newData = data.map(item =>
            item.idGlobal === editingId
                ? { ...editingRow, dateModified: DateTime.now() }
                : item
        );

        if (editingRow.isNew) {
            onSaveSuccess?.(editingRow.idGlobal);
        }

        onChange(newData);
        setEditingId(null);
        setEditingRow(null);
    };

    const updateEditingField = (field: keyof IIsgGoods, value: any) => {
        if (editingRow) {
            setEditingRow({ ...editingRow, [field]: value });
        }
    };

    const addNewRow = () => {
        if (disabled) return;
        if (isEditing) {
            renderGlobalAlert({
                variant: "info",
                statusCode: 200,
                title: baseT("editWarning")
            })
            return;
        }
        const newRow: IIsgGoods = {
            idGlobal: uuidv4(),
            dateCreated: DateTime.now(),
            dateModified: DateTime.now(),
            dateDeleted: null,
            idContractorMotivationGlobal: contractorMotivationId || '',
            days: 0,
            koeffValue: 0,
            priceCode: 1,
            isNew: true,
        };
        onChange([...data, newRow]);
        setEditingId(newRow.idGlobal);
        setEditingRow(newRow);
    };

    const deleteRow = (id: string) => {
        if (disabled) return;
        const record = data.find(item => item.idGlobal === id);

        if (record?.isNew) {
            const newData = data.filter(item => item.idGlobal !== id);
            onChange(newData);
        } else {
            const newData = data.map(item =>
                item.idGlobal === id
                    ? { ...item, dateDeleted: DateTime.now() }
                    : item
            );
            onChange(newData);
        }
    };

    const restoreRow = (id: string) => {
        if (disabled) return;
        const newData = data.map(item =>
            item.idGlobal === id
                ? { ...item, dateDeleted: null, dateModified: DateTime.now() }
                : item
        );
        onChange(newData);
    };

    const columns: ColumnsType<IIsgGoods> = [
        {
            title: tableName === 'isgGoods' ? baseT('goodsTable.columns.daysIsg') : baseT('goodsTable.columns.daysilliquid'),
            dataIndex: 'days',
            key: 'days',
            width: 220,
            render: (text: number, record: IIsgGoods) => {
                if (editingId === record.idGlobal) {
                    return (
                        <input
                            className={styles.input}
                            type="number"
                            min={0}
                            value={editingRow?.days || 0}
                            onChange={(e) => updateEditingField('days', parseInt(e.target.value) || 0)}
                            onFocus={(e) => e.target.select()}
                        />
                    );
                }
                return <span>{text}</span>;
            },
        },
        {
            title: baseT('goodsTable.columns.koeffValue'),
            dataIndex: 'koeffValue',
            key: 'koeffValue',
            width: 120,
            render: (text: number, record: IIsgGoods) => {
                if (editingId === record.idGlobal) {
                    return (
                        <input
                            className={styles.input}
                            type="number"
                            step="0.01"
                            min={0}
                            value={editingRow?.koeffValue || 0}
                            onChange={(e) => updateEditingField('koeffValue', parseFloat(e.target.value) || 0)}
                            onFocus={(e) => e.target.select()}
                        />
                    );
                }
                return <span>{text}</span>;
            },
        },
        {
            title: baseT('goodsTable.columns.priceCode'),
            dataIndex: 'priceCode',
            key: 'priceCode',
            width: 250,
            render: (text: number, record: IIsgGoods) => {
                if (editingId === record.idGlobal) {
                    return (
                        <Select
                            value={editingRow?.priceCode}
                            onChange={(value) => updateEditingField('priceCode', value)}
                            options={priceCodeOptions}
                        />
                    );
                }
                return <span>{getPriceCodeText(text)}</span>;
            },
        },
        {
            title: baseT('goodsTable.columns.dateModified'),
            dataIndex: 'dateModified',
            key: 'dateModified',
            width: 150,
            render: (text: DateTime) => moment(text).format('DD.MM.YYYY'),
        },
        {
            title: baseT('goodsTable.columns.dateDeleted'),
            dataIndex: 'dateDeleted',
            key: 'dateDeleted',
            width: 150,
            render: (text: string | null) => text ? moment(text).format('DD.MM.YYYY') : '',
        },
        {
            title: baseT('goodsTable.columns.actions'),
            key: 'action',
            width: 150,
            render: (_: any, record: IIsgGoods) => {
                if (disabled) return;
                const deleted = isRowDeleted(record);
                if (deleted) {
                    return (
                        <Space size="small">
                            <Button
                                type="text"
                                onClick={() => restoreRow(record.idGlobal)}
                                icon={<UndoOutlined style={{ color: '#1890ff' }} />}
                                size="small"
                                title={baseT('goodsTable.actions.restore')}
                            />
                        </Space>
                    );
                }
                if (editingId === record.idGlobal) {
                    return (
                        <Space size="small">
                            <Button
                                type="text"
                                onClick={saveEdit}
                                icon={<CheckOutlined style={{ color: '#009933' }} />}
                                size="small"
                                title={baseT('goodsTable.actions.save')}
                            />
                            <Button
                                type="text"
                                onClick={cancelEdit}
                                icon={<CloseOutlined style={{ color: '#cc3333' }} />}
                                size="small"
                                title={baseT('goodsTable.actions.cancel')}
                            />
                        </Space>
                    );
                }
                return (
                    <Space size="small">
                        <Button
                            type="text"
                            onClick={() => startEdit(record)}
                            icon={<EditOutlined/>}
                            size="small"
                            title={baseT('goodsTable.actions.edit')}
                            disabled={disabled || isEditing}
                        />
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined style={{ color: '#cc3333' }}/>}
                            size="small"
                            title={baseT('goodsTable.actions.delete')}
                            onClick={() =>
                                setShowMessageModal({
                                    show: true,
                                    message: baseT('goodsTable.confirm.deleteTitle'),
                                    hideCancelButton: false,
                                    idRow: record.idGlobal
                                })
                            }
                        />
                    </Space>
                );
            },
        },
    ];

    useEffect(() => {
        onEditingChange?.(isEditing);
    }, [isEditing, onEditingChange]);

    return (
        <>
            <div>
                <div className={styles.plusButtonWrapper}>
                    <Button
                        type="text"
                        onClick={addNewRow}
                        icon={<PlusOutlined style={{ color: '#009933' }} />}
                        size="small"
                        disabled={disabled}
                    >{baseT('goodsTable.actions.add')}</Button>
                </div>
                <Table
                    className={styles.table}
                    columns={columns}
                    dataSource={data}
                    rowKey="idGlobal"
                    pagination={false}
                    bordered
                    size="middle"
                    locale={{ emptyText: baseT('goodsTable.emptyText') }}
                />
            </div>
            {showMessageModal.show && (
                <MessageModalWindow
                    ok={{
                        onClick: () => {
                            deleteRow(showMessageModal.idRow as string);
                            setShowMessageModal({ show: false, message: "", idRow: "" });
                        },
                    }}
                    cancel={{
                        onClick: () => setShowMessageModal({ show: false, message: "", idRow: "" }),
                        hidden: false,
                    }}
                    message={showMessageModal.message}
                />
            )}
        </>
    );
};

export default GoodsTable;